import { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import ReCAPTCHA from "react-google-recaptcha";
import { addDoc, collection, getDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import Button from "../button/button";
import { regionsEtDepartements } from "./RegionsEtDepartements";
import { API_BASE_URL } from "../../api/api";

const SvgCheck = () => (
  <svg width="40" height="40" viewBox="0 0 28 28" fill="#321e35" xmlns="http://www.w3.org/2000/svg">
    <path d="M14.0003 0.666504C6.64033 0.666504 0.666992 6.63984 0.666992 13.9998C0.666992 21.3598 6.64033 27.3332 14.0003 27.3332C21.3603 27.3332 27.3337 21.3598 27.3337 13.9998C27.3337 6.63984 21.3603 0.666504 14.0003 0.666504ZM14.0003 24.6665C8.12033 24.6665 3.33366 19.8798 3.33366 13.9998C3.33366 8.11984 8.12033 3.33317 14.0003 3.33317C19.8803 3.33317 24.667 8.11984 24.667 13.9998C24.667 19.8798 19.8803 24.6665 14.0003 24.6665ZM20.1203 8.1065L11.3337 16.8932L7.88033 13.4532L6.00033 15.3332L11.3337 20.6665L22.0003 9.99984L20.1203 8.1065Z" />
  </svg>
);
export default function ContactForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState("");
  const [departementValue, setDepartementValue] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [status, setStatus] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const [recaptchaSize, setRecaptchaSize] = useState("compact");

  const recaptchaRef = useRef();

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    formState: { errors, isSubmitted },
  } = useForm({ mode: "onChange" });

  useEffect(() => {
    setValue("region", selectedRegion);
    setValue("departement", departementValue);
    if (isSubmitted) {
      trigger("region");
      trigger("departement");
    }
  }, [selectedRegion, departementValue, setValue, trigger, isSubmitted]);

  useEffect(() => {
    const handleResize = () => {
      const isSmallScreen = window.innerWidth < 500;
      setRecaptchaSize(isSmallScreen ? "compact" : "normal");
    };

    handleResize(); // appel initial
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getFieldClass = name => (isSubmitted && errors[name] ? "error" : "");
  const onSubmit = async data => {
    const isValid = await trigger();
    if (!isValid) return;

    if (!captchaToken) {
      setStatus("Veuillez valider le reCAPTCHA.");
      return;
    }

    setIsLoading(true); // ⬅️ Début du spinner
    data.captchaToken = captchaToken;

    try {
      const docRef = await addDoc(collection(db, "messages"), {
        ...data,
        captchaToken: captchaToken,
        date: serverTimestamp(),
      });

      const docSnap = await getDoc(doc(db, "messages", docRef.id));
      if (docSnap.exists()) {
        const timestamp = docSnap.data().date;
        console.log("Date récupérée :", timestamp.toDate().toISOString());
      }

      await sendEmail(data);
      setStatus("");
      setIsModalOpen(true); // ⬅️ Affiche la modal
    } catch (error) {
      console.error("Erreur Firestore:", error);
      setStatus("Erreur lors de l'envoi.");
    } finally {
      setIsLoading(false); // ⬅️ Fin du spinner
      recaptchaRef.current.reset();
      setCaptchaToken(null);
    }
  };

  const sendEmail = async data => {
    try {
      const response = await fetch(`${API_BASE_URL}/send-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      const result = await response.json();
      if (!result.success) throw new Error("Erreur d'envoi email");
    } catch (error) {
      console.error("Erreur requête :", error);
      setStatus("Une erreur est survenue lors de l’envoi de l’e-mail.");
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="forms">
        {/* Nom, Prénom, Entreprise, Email */}
        {[
          {
            name: "nom",
            label: "Votre nom",
            pattern: /^[A-Za-zÀ-ÿ -]+$/,
            placeholder: "Le champ nom est obligatoire",
          },
          {
            name: "prenom",
            label: "Votre prénom",
            pattern: /^[A-Za-zÀ-ÿ -]+$/,
            placeholder: "Le champ prénom est obligatoire",
          },
          {
            name: "entreprise",
            label: "Nom de votre l'entreprise",
            placeholder: "Le champ nom de l'entreprise est obligatoire",
          },
          {
            name: "email",
            label: "Votre e-mail",
            type: "email",
            pattern: /^\S+@\S+\.\S+$/,
            placeholder: "Le champ e-mail est obligatoire",
          },
        ].map(({ name, label, pattern, type = "text", placeholder }) => (
          <div className="forms-fieldsInput" key={name}>
            <label htmlFor={name}>{label}</label>
            <input
              {...register(name, {
                required: `Ce champ est requis`,
                pattern: pattern && {
                  value: pattern,
                  message: "Format invalide",
                },
              })}
              name={name}
              type={type}
              id={name}
              className={getFieldClass(name)}
            />
            {errors[name] && <p className="text-red-500">{errors[name].message && placeholder}</p>}
          </div>
        ))}

        {/* Région */}
        <div className="forms-fieldsInput">
          <label htmlFor="region">Votre région</label>
          <select
            {...register("region", {
              required: "Le champ région est obligatoire",
            })}
            id="region"
            name="region"
            className={getFieldClass("region")}
            onChange={e => {
              setSelectedRegion(e.target.value);
              // setSelectedDepartement("");
              setDepartementValue("");
            }}
            value={selectedRegion}
          >
            <option value="">Sélectionnez une région</option>
            {Object.keys(regionsEtDepartements).map(region => (
              <option key={region} value={region}>
                {region}
              </option>
            ))}
          </select>
          {errors.region && <p className="text-red-500">{errors.region.message}</p>}
        </div>
        {/* Département*/}
        <div className="forms-fieldsInput">
          <label htmlFor="departement">Votre département</label>
          <select
            {...register("departement", {
              required: "Le champ département est obligatoire",
            })}
            name="departement"
            id="departement"
            className={getFieldClass("departement")}
            value={departementValue}
            onChange={e => setDepartementValue(e.target.value)}
            disabled={!selectedRegion}
            aria-describedby="Votre région doit d'abord être séléctionné"
          >
            <option value="">
              {!selectedRegion ? "Sélectionnez un département" : "Sélectionnez un département"}
            </option>
            {selectedRegion &&
              regionsEtDepartements[selectedRegion].map(dep => (
                <option key={dep.code} value={`${dep.code} - ${dep.nom}`}>
                  {dep.code} - {dep.nom}
                </option>
              ))}
          </select>
          {errors.departement && <p className="text-red-500">{errors.departement.message}</p>}
        </div>

        {/* Message */}
        <div id="divMessage" className="forms-fieldsInput">
          <label htmlFor="message">Votre message</label>
          <textarea
            {...register("message", {
              required: "Le champ message est obligatoire",
              onChange: e => e.target.value.length,
            })}
            id="message"
            name="message"
            className={getFieldClass("message")}
          />
          {errors.message && <p className="text-red-500">{errors.message.message}</p>}
        </div>

        <ReCAPTCHA
          sitekey="6Ledo0wrAAAAAAT1W-m0SOuZrHMRxXUyostWMlSx"
          ref={recaptchaRef}
          onChange={token => setCaptchaToken(token)}
          id="recaptcha_image"
          size={recaptchaSize}
        />

        <Button type="submit" contains="Envoyer" />
        {status && (
          <p className={`status-message ${status.includes("succès") ? "success" : "error"}`}>
            {status}
          </p>
        )}
      </form>
      {isLoading && (
        <div className="spinner-overlay">
          <div className="spinner">
            <div className="dot dot1" />
            <div className="dot dot2" />
            <div className="dot dot3" />
            <div className="dot dot4" />
            <div className="dot dot5" />
            <div className="dot dot6" />
            <div className="dot dot7" />
            <div className="dot dot8" />
          </div>
        </div>
      )}

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <SvgCheck />
            <p>Votre message a bien été envoyé.</p>
            <Button contains="Fermer" type="button" onClick={() => window.location.reload()} />
          </div>
        </div>
      )}
    </>
  );
}
